import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Calendar as CalendarComp } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon, Clock, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SLOTS = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00"];

export default function Booking() {
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", consult_type: "general", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) { toast.error("Pick a date + time slot"); return; }
    setBusy(true);
    try {
      const dt = new Date(date);
      const [h, m] = slot.split(":").map(Number);
      dt.setHours(h, m, 0, 0);
      await api.post("/bookings", { ...form, booking_datetime: dt.toISOString() });
      setDone(true);
      toast.success("Booking confirmed. You'll receive a text + email reminder.");
    } catch { toast.error("Couldn't book."); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-[700px] mx-auto px-5 py-24 text-center" data-testid="booking-success">
        <CheckCircle2 className="w-14 h-14 mx-auto mb-5 text-emerald-400" />
        <h2 className="font-display text-4xl font-light tracking-tighter mb-3">Booked.</h2>
        <p className="text-gray-400">A broker will call at the scheduled time. We'll text a reminder 1 hour before.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-5 lg:px-10 py-16" data-testid="booking-page">
      <p className="label-mono mb-3">Book a Consultation</p>
      <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-5">20 minutes. Real answers.</h1>
      <p className="text-gray-400 max-w-2xl mb-12">A 555 broker reviews your situation, walks through deal options, and outlines the path forward. Free, no obligation.</p>

      <form onSubmit={submit} className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 glass rounded-2xl p-7">
          <div className="label-mono mb-4 flex items-center gap-2"><CalendarIcon className="w-4 h-4" /> Pick a date</div>
          <CalendarComp
            mode="single"
            selected={date}
            onSelect={setDate}
            disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || d.getDay() === 0}
            className="bg-transparent text-white"
            data-testid="booking-calendar"
          />
          <div className="mt-7">
            <div className="label-mono mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Time slot</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {SLOTS.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => setSlot(s)}
                  data-testid={`booking-slot-${s}`}
                  className={`py-2.5 rounded-full text-xs border ${slot === s ? "silver-gradient text-[#050507] border-transparent" : "border-white/10 text-gray-300 hover:bg-white/5"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 glass rounded-2xl p-7 space-y-3">
          <p className="label-mono">Your Details</p>
          <Input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="booking-name" />
          <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="booking-email" />
          <Input required placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="booking-phone" />
          <select value={form.consult_type} onChange={(e) => setForm({ ...form, consult_type: e.target.value })} className="w-full bg-[#0A0A0E] border border-[#1A1A24] h-12 rounded-xl text-white px-3 text-sm" data-testid="booking-type">
            <option value="general">General consultation</option>
            <option value="lease">Lease deal review</option>
            <option value="finance">Financing strategy</option>
            <option value="sourcing">Vehicle sourcing</option>
            <option value="trade">Trade-in valuation</option>
          </select>
          <textarea rows={4} placeholder="Anything we should know?" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-[#0A0A0E] border border-[#1A1A24] rounded-xl text-white p-3 text-sm" data-testid="booking-notes" />
          <button type="submit" disabled={busy} className="w-full silver-gradient text-[#050507] font-bold py-3.5 rounded-full disabled:opacity-50" data-testid="booking-submit">
            {busy ? "Booking…" : "Confirm Booking"}
          </button>
          <p className="text-[10px] text-gray-500">You'll receive an email + text confirmation. Cancel anytime.</p>
        </div>
      </form>
    </div>
  );
}
